/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { getBirthDateString } from '@sportninja/common/reducers/helpers'
import dayjs from 'dayjs'
import { memo } from 'react'
import { Link } from 'react-router-dom'

import { font } from '../../css'
import Icon from '../../Icon'
import { Flex } from '../../Layout'
import { existingDecision, orderedCandidates } from './utils/duplicateDecisions'

/**
 * One radio choice. Plain markup on purpose: rowStyles styles every choice of
 * the row once, so ticking "create all" re-renders 1,000+ rows without
 * serializing a style per choice.
 */
const Choice = ({ children, name, value, checked, onSelect, rightContent }) => {
  return (
    <div className='choice'>
      <label>
        <input
          type='radio'
          name={name}
          value={value}
          checked={checked}
          onChange={onSelect}
        />
        {children}
      </label>
      {rightContent}
    </div>
  )
}

const Pip = () => <div css={pipStyles} />

const SmallText = ({ children, isMismatched }) => {
  return (
    <>
      {children && <Pip />}

      <div css={isMismatched ? smallTextMismatchedStyles : smallTextStyles}>
        {children}
      </div>
    </>
  )
}

/** The flagged row as the file has it. Static while the admin decides, so memoized. */
const DuplicateRowHeader = memo(function DuplicateRowHeader({
  player,
  playerTypes,
  teamName,
  shouldHighlightDiff,
  importTeamName,
  importTeamRosterName,
}) {
  const {
    sn_player_id,
    birth_date,
    first_name,
    last_name,
    player_conflicts,
    player_type_id,
  } = player

  const hasPlayerTypeMismatch =
    shouldHighlightDiff &&
    player_conflicts.reduce((collector, conflict) => {
      if (collector) {
        return collector
      }
      const value = conflict?.team_player_rosters?.[0]?.pivot?.player_type_id
      if (!value) {
        return collector
      }
      return player_type_id !== value
    }, false)

  const hasBirthDateMismatch =
    shouldHighlightDiff &&
    player_conflicts.reduce((collector, conflict) => {
      if (collector) {
        return collector
      }
      const value = conflict.birth_date
      if (!value) {
        return collector
      }
      return dayjs(birth_date).isSame(dayjs(value))
    }, false)

  const hasPlayerIdMismatch =
    shouldHighlightDiff &&
    player_conflicts.reduce((collector, conflict) => {
      if (collector) {
        return collector
      }
      const value = conflict.uid
      if (!value) {
        return collector
      }
      return sn_player_id !== value
    }, false)

  const birthDateAsString = getBirthDateString(birth_date)

  return (
    <Flex alignItems='center'>
      <div
        css={css`
          ${font.title}
          font-weight: 500;
          font-size: 20px;
          margin-bottom: 4px;
        `}
      >
        {first_name} {last_name}
      </div>
      {teamName ? <SmallText>{teamName}</SmallText> : null}
      {importTeamName ? <SmallText>{importTeamName}</SmallText> : null}
      {importTeamRosterName ? (
        <SmallText>{importTeamRosterName}</SmallText>
      ) : null}
      {birth_date && (
        <SmallText isMismatched={hasBirthDateMismatch}>
          {birthDateAsString?.toUpperCase()?.includes('INVALID DATE')
            ? `Invalid Date | ${birth_date}`
            : birthDateAsString}
        </SmallText>
      )}
      <SmallText isMismatched={hasPlayerTypeMismatch}>
        {playerTypes?.find((type) => type?.id === player_type_id)?.name_full}
      </SmallText>

      <SmallText isMismatched={hasPlayerIdMismatch}>{sn_player_id}</SmallText>
    </Flex>
  )
})

/** One existing player the row may match. Static while the admin decides, so memoized. */
const CandidateDetails = memo(function CandidateDetails({
  conflict,
  player,
  playerTypes,
  shouldHighlightDiff,
}) {
  const { first_name, last_name, player_type_id, sn_player_id } = player
  const {
    birth_date,
    name_first,
    name_last,
    team_player_rosters,
    uid,
    import_2_id = '',
  } = conflict

  const teamRosterStrings = team_player_rosters
    .map((roster) => {
      return roster?.name
    })
    .join(', ')

  const teamStrings = team_player_rosters
    .map((roster) => {
      return roster?.team?.name
    })
    .join(', ')

  const existingPlayerTypeId = team_player_rosters?.[0]?.pivot?.player_type_id

  const birthDateAsString = getBirthDateString(birth_date)

  return (
    <>
      <div
        css={css`
          ${first_name !== name_first ||
          (last_name !== name_last &&
            css`
              color: ${colors.DEFAULT_FLAIR};
            `)}
        `}
      >
        {name_first} {name_last}
      </div>
      {birth_date ? (
        <SmallText
          isMismatched={
            shouldHighlightDiff &&
            !dayjs(birth_date).isSame(dayjs(conflict.birth_date))
          }
        >
          {birthDateAsString?.toUpperCase()?.includes('INVALID DATE')
            ? `Invalid Date | ${birth_date}`
            : birthDateAsString}
        </SmallText>
      ) : null}
      <SmallText>{teamStrings ? teamStrings : 'No Team'}</SmallText>
      {teamRosterStrings ? <SmallText>{teamRosterStrings}</SmallText> : null}
      {existingPlayerTypeId && (
        <SmallText
          isMismatched={
            shouldHighlightDiff && existingPlayerTypeId !== player_type_id
          }
        >
          {
            playerTypes.find((type) => type?.id === existingPlayerTypeId)
              ?.name_full
          }
        </SmallText>
      )}

      <SmallText isMismatched={shouldHighlightDiff && uid !== sn_player_id}>
        {uid}
      </SmallText>
      {import_2_id && import_2_id !== '' ? (
        <SmallText isMismatched={shouldHighlightDiff && uid !== sn_player_id}>
          {`Salesforce-id: ${import_2_id}`}
        </SmallText>
      ) : null}
    </>
  )
})

const CandidateLink = memo(function CandidateLink({ uid }) {
  return (
    <Link
      rel='noreferrer'
      target='_blank'
      to={`${ROUTES.PLAYER_ROOT}/${uid}`}
      className='link'
      css={linkStyles}
    >
      <Icon
        name='external-link'
        fontSize={12}
        color={colors.DEFAULT_FLAIR}
        css={linkIconStyles}
      />
      Open
    </Link>
  )
})

/**
 * One flagged row of the duplicate review: what the file says, then a choice
 * between each matching player (those already on this roster first), a new
 * player, and not importing the row.
 *
 * Memoized: every prop is stable across the review (`dispatch` comes straight
 * from useReducer), so choosing on one row re-renders that row alone.
 */
const ImportTeamRosterDuplicatePlayer = ({
  line,
  player,
  decision,
  dispatch,
  playerTypes,
  teamName,
  shouldHighlightDiff,
  importPlayerId,
  importTeamName,
  importTeamRosterName,
}) => {
  const { sn_player_id } = player

  const isUpdatingExistingPlayer =
    typeof sn_player_id === 'string' &&
    typeof importPlayerId === 'string' &&
    sn_player_id.length > 0 &&
    importPlayerId.length > 0 &&
    sn_player_id === importPlayerId

  const decide = (next) => dispatch({ type: 'decide', line, decision: next })

  return (
    <div css={rowStyles}>
      <DuplicateRowHeader
        player={player}
        playerTypes={playerTypes}
        teamName={teamName}
        shouldHighlightDiff={shouldHighlightDiff}
        importTeamName={importTeamName}
        importTeamRosterName={importTeamRosterName}
      />
      <div>
        {orderedCandidates(player).map((conflict) => {
          const value = existingDecision(conflict.uid)

          return (
            <Choice
              key={conflict?.uid}
              name={line}
              value={value}
              checked={decision === value}
              onSelect={() => decide(value)}
              rightContent={<CandidateLink uid={conflict.uid} />}
            >
              <CandidateDetails
                conflict={conflict}
                player={player}
                playerTypes={playerTypes}
                shouldHighlightDiff={shouldHighlightDiff}
              />
            </Choice>
          )
        })}
        <Choice
          name={line}
          value='create'
          checked={decision === 'create'}
          onSelect={() => decide('create')}
        >
          {isUpdatingExistingPlayer ? (
            <>
              Update existing player
              <SmallText>{importPlayerId}</SmallText>
            </>
          ) : (
            'Create a new player'
          )}
        </Choice>
        <Choice
          name={line}
          value='ignore'
          checked={decision === 'ignore'}
          onSelect={() => decide('ignore')}
        >
          Ignore (do not import)
        </Choice>
      </div>
    </div>
  )
}

export default memo(ImportTeamRosterDuplicatePlayer)

// ---- styles ----
// Module-level so each is serialized once, not on every render of a row.

const rowStyles = css`
  margin-left: 8px;

  .choice {
    display: flex;
    flex: 1;
    align-items: center;
    min-height: 26px;

    label {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      min-height: inherit;
      cursor: pointer;
      padding: 0 8px;
      margin-left: -8px;
      border-radius: 4px;

      transition: background-color 0.2s ease-in-out;

      &:hover {
        background-color: rgba(255, 255, 255, 0.05);
      }
    }

    input[type='radio'] {
      margin: 0 8px 0 0;
      font: inherit;
      color: white;
      width: 14px;
      height: 14px;
      border: 1.5px solid white;
      border-radius: 50%;
      display: grid;
      place-content: center;

      &::before {
        content: '';
        width: 8px;
        height: 8px;
        border-radius: 50%;
        transform: scale(0);
        transition: 120ms transform ease-in-out;
        box-shadow: inset 1em 1em ${colors.DEFAULT_FLAIR};
      }

      &:checked {
        border-color: ${colors.DEFAULT_FLAIR};
        ::before {
          transform: scale(1);
        }
      }
    }

    .link {
      opacity: 0;
      transition: opacity 0.2s ease-in-out;
    }

    &:hover .link {
      opacity: 1;
    }
  }
`

const pipStyles = css`
  height: 4px;
  width: 4px;
  background-color: ${colors.PALE_SKY};
  border-radius: 50%;
  margin-left: 8px;
`

const smallTextStyles = css`
  margin-left: 8px;
  font-size: 14px;
  color: #9fa3a9;
`

const smallTextMismatchedStyles = css`
  margin-left: 8px;
  font-size: 14px;
  color: ${colors.DEFAULT_FLAIR};
`

const linkStyles = css`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 8px;
  transition: background-color 0.2s ease-in-out;
  border-radius: 4px;

  margin-left: auto;
  font-size: 14px;
  color: ${colors.DEFAULT_FLAIR};

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    color: white;
    i {
      color: white;
    }
  }
`

const linkIconStyles = css`
  margin-right: 8px;
`
